import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  useTheme,
  Alert,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import NoteIcon from '@mui/icons-material/Note';
import AllDealsDialog from '../components/AllDealsDialog';
import DealSummaryTable from '../components/DealSummaryTable';
import RecentDealsList from '../components/RecentDealsList';
import TodoList from '../components/TodoList';
import DashboardLayout from '../components/DashboardLayout';
import proxyClient from '../utils/proxyClient';

interface Deal {
  id: string;
  deal_number: string;
  deal_type: string;
  bfs_stage: string;
  mortgage_amount: string;
  mtg_type: string;
  term: string;
  ltv: string;
  submission_date: string;
  completion_date: string | null;
  closing_date: string | null;
  cof: string | null;
  elapsed_time: number | null;
  notes: string | null;
}

interface PieChartDataItem {
  name: string;
  value: number;
  color: string;
}

// Function to create styled pie chart categories
const createPieChartData = (deals: Deal[]): PieChartDataItem[] => {
  const completedDeals = deals.filter(deal => deal.elapsed_time !== null);
  const categories = [
    { name: '<10 min', color: '#4caf50' },
    { name: '10–25 min', color: '#2196f3' },
    { name: '25–35 min', color: '#ff9800' },
    { name: '>35 min', color: '#f44336' },
  ];

  // Initialize count to 0 for all categories
  const dataMap = categories.reduce((acc, cat) => {
    acc[cat.name] = { ...cat, value: 0 };
    return acc;
  }, {} as Record<string, PieChartDataItem>);

  // Count deals in each category
  completedDeals.forEach(deal => {
    // Convert elapsed_time from seconds to minutes if needed
    const seconds = deal.elapsed_time || 0;
    const minutes = seconds / 60; // Convert seconds to minutes
    
    if (minutes < 10) dataMap['<10 min'].value++;
    else if (minutes < 25) dataMap['10–25 min'].value++;
    else if (minutes < 35) dataMap['25–35 min'].value++;
    else dataMap['>35 min'].value++;
  });

  // Convert map to array for pie chart
  return Object.values(dataMap);
};

// Format date function
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'No date';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return 'Invalid date';
  }
};

// Add this helper function to calculate days remaining until closing date
const getDaysRemaining = (closingDate: string | null): number | null => {
  if (!closingDate) return null;
  
  try {
    const closing = new Date(closingDate);
    const today = new Date();
    
    // Reset the time portion to ensure we're comparing full days
    closing.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    // Calculate the difference in milliseconds and convert to days
    const diffTime = closing.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    return null;
  }
};

// Add this function to get color based on days remaining
const getClosingDateColor = (daysRemaining: number | null): string => {
  if (daysRemaining === null) return 'text.secondary'; // Default color for no date
  
  if (daysRemaining < 0) return 'text.disabled'; // Grey for past dates
  if (daysRemaining <= 7) return 'error.main'; // Red for 7 or fewer days
  if (daysRemaining <= 14) return 'warning.main'; // Orange for 8-14 days
  return 'success.main'; // Green for more than 14 days
};

// Add this function to get icon color based on days remaining
const getClosingIconColor = (daysRemaining: number | null): string => {
  if (daysRemaining === null) return 'text.secondary'; // Default color for no date
  
  if (daysRemaining < 0) return 'text.disabled'; // Grey for past dates
  if (daysRemaining <= 7) return 'error.main'; // Red for 7 or fewer days
  if (daysRemaining <= 14) return 'warning.main'; // Orange for 8-14 days
  return 'success.main'; // Green for more than 14 days
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [bfsCount, setBfsCount] = useState(0);
  const [pieChartData, setPieChartData] = useState<PieChartDataItem[]>([]);
  const [openAllDeals, setOpenAllDeals] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<Deal | null>(null);
  
  // Notes editing states
  const [notesAnchorEl, setNotesAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentDealForNotes, setCurrentDealForNotes] = useState<Deal | null>(null);
  const [noteText, setNoteText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesEditMode, setNotesEditMode] = useState(false);
  
  // COF date editing states
  const [cofAnchorEl, setCofAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentDealForCof, setCurrentDealForCof] = useState<Deal | null>(null);
  const [selectedCofDate, setSelectedCofDate] = useState<Date | null>(null);
  const [savingCofDate, setSavingCofDate] = useState(false);

  // BFS stage editing states
  const [bfsStageAnchorEl, setBfsStageAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentDealForBfsStage, setCurrentDealForBfsStage] = useState<Deal | null>(null);
  const [selectedBfsStage, setSelectedBfsStage] = useState<string>('');
  const [savingBfsStage, setSavingBfsStage] = useState(false);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        
        const { data: sessionData } = await proxyClient.auth.getSession();
        const token = sessionData.session?.access_token || '';
        
        const response = await fetch('/api/borrower-data/deals', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch deals: ${response.statusText}`);
        }
        
        const data = await response.json();

        if (data) {
          const formattedDeals = data.map(deal => ({
            id: deal.id,
            deal_number: deal.deal_number || 'N/A',
            deal_type: deal.deal_type || 'N/A',
            bfs_stage: deal.bfs_stage || 'N/A',
            mortgage_amount: deal.mortgage_amount || 'N/A',
            mtg_type: deal.mtg_type || 'N/A',
            term: deal.term || 'N/A',
            ltv: deal.ltv || 'N/A',
            submission_date: deal.submission_date || new Date().toISOString(),
            completion_date: deal.completion_date || null,
            closing_date: deal.closing_date || null,
            cof: deal.cof || null,
            elapsed_time: deal.elapsed_time || null,
            notes: deal.notes || null,
          }));

          setDeals(formattedDeals);
          const bfsDeals = formattedDeals.filter(deal => deal.bfs_stage && deal.bfs_stage !== 'N/A');
          setBfsCount(bfsDeals.length);
          setPieChartData(createPieChartData(formattedDeals));
        }
      } catch (err: any) {
        console.error('Error fetching deals:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAllDeals = () => setOpenAllDeals(true);
  const handleCloseAllDeals = () => setOpenAllDeals(false);

  const handleDeleteClick = (deal: Deal) => {
    setDealToDelete(deal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDealToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!dealToDelete) return;

    try {
      setLoading(true);
      
      const { data: sessionData } = await proxyClient.auth.getSession();
      const token = sessionData.session?.access_token || '';
      
      const response = await fetch(`/api/borrower-data/deals/${dealToDelete.deal_number}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete deal: ${response.statusText}`);
      }
      
      setDeals(prevDeals => prevDeals.filter(deal => deal.deal_number !== dealToDelete.deal_number));
      setDeleteDialogOpen(false);
      setDealToDelete(null);
      
    } catch (err: any) {
      console.error('Error deleting deal:', err);
      setError(err.message || 'Failed to delete deal');
    } finally {
      setLoading(false);
    }
  };

  // Notes handling functions
  const handleNotesClick = (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => {
    setCurrentDealForNotes(deal);
    setNoteText(deal.notes || '');
    setNotesAnchorEl(event.currentTarget);
    setNotesEditMode(false);
  };

  const handleNotesClose = () => {
    setNotesAnchorEl(null);
    setCurrentDealForNotes(null);
    setNotesEditMode(false);
  };

  const toggleNotesEditMode = () => {
    setNotesEditMode(prev => !prev);
  };

  const handleSaveNotes = async () => {
    if (!currentDealForNotes) return;
    
    try {
      setSavingNotes(true);
      const { data: sessionData } = await proxyClient.auth.getSession();
      const token = sessionData.session?.access_token || '';
      
      const response = await fetch(`/api/borrower-data/deals/${currentDealForNotes.deal_number}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notes: noteText })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save notes');
      }
      
      // Update the local state with the new notes
      setDeals(prevDeals => 
        prevDeals.map(deal => 
          deal.deal_number === currentDealForNotes.deal_number 
            ? { ...deal, notes: noteText } 
            : deal
        )
      );
      
      handleNotesClose();
    } catch (err: any) {
      console.error('Error saving notes:', err);
      setError(err.message || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // COF date handling functions
  const handleCofClick = (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => {
    setCurrentDealForCof(deal);
    setSelectedCofDate(deal.cof ? new Date(deal.cof) : null);
    setCofAnchorEl(event.currentTarget);
  };

  const handleCofClose = () => {
    setCofAnchorEl(null);
    setCurrentDealForCof(null);
    setSelectedCofDate(null);
  };

  const handleSaveCof = async () => {
    if (!currentDealForCof) return;
    
    try {
      setSavingCofDate(true);
      const { data: sessionData } = await proxyClient.auth.getSession();
      const token = sessionData.session?.access_token || '';
      
      const formattedDate = selectedCofDate ? selectedCofDate.toISOString().split('T')[0] : null;
      
      const response = await fetch(`/api/borrower-data/deals/${currentDealForCof.deal_number}/cof`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cof: formattedDate })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save COF date');
      }
      
      // Update the local state with the new COF date
      setDeals(prevDeals => 
        prevDeals.map(deal => 
          deal.deal_number === currentDealForCof.deal_number 
            ? { ...deal, cof: formattedDate } 
            : deal
        )
      );
      
      handleCofClose();
    } catch (err: any) {
      console.error('Error saving COF date:', err);
      setError(err.message || 'Failed to save COF date');
    } finally {
      setSavingCofDate(false);
    }
  };

  // BFS stage handling functions
  const handleBfsStageClick = (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => {
    setCurrentDealForBfsStage(deal);
    setSelectedBfsStage(deal.bfs_stage || '');
    setBfsStageAnchorEl(event.currentTarget);
  };

  const handleBfsStageClose = () => {
    setBfsStageAnchorEl(null);
    setCurrentDealForBfsStage(null);
    setSelectedBfsStage('');
  };

  const handleSaveBfsStage = async () => {
    if (!currentDealForBfsStage) return;
    
    try {
      setSavingBfsStage(true);
      const { data: sessionData } = await proxyClient.auth.getSession();
      const token = sessionData.session?.access_token || '';
      
      const response = await fetch(`/api/borrower-data/deals/${currentDealForBfsStage.deal_number}/bfs-stage`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ bfs_stage: selectedBfsStage })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save BFS stage');
      }
      
      // Update the local state with the new BFS stage
      setDeals(prevDeals => 
        prevDeals.map(deal => 
          deal.deal_number === currentDealForBfsStage.deal_number 
            ? { ...deal, bfs_stage: selectedBfsStage } 
            : deal
        )
      );
      
      // Update BFS count if needed
      const updatedDeals = [...deals];
      const dealIndex = updatedDeals.findIndex(d => d.deal_number === currentDealForBfsStage.deal_number);
      if (dealIndex !== -1) {
        updatedDeals[dealIndex] = { ...updatedDeals[dealIndex], bfs_stage: selectedBfsStage };
        const bfsDeals = updatedDeals.filter(deal => deal.bfs_stage && deal.bfs_stage !== 'N/A');
        setBfsCount(bfsDeals.length);
      }
      
      handleBfsStageClose();
    } catch (err: any) {
      console.error('Error saving BFS stage:', err);
      setError(err.message || 'Failed to save BFS stage');
    } finally {
      setSavingBfsStage(false);
    }
  };

  const renderStatusChip = (bfsStage: string) => {
    if (!bfsStage || bfsStage === 'N/A') return <Chip size="small" label="Not Started" color="default" />;

    const stage = bfsStage.toLowerCase();
    const stageMap: { [key: string]: { label: string; color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } } = {
      submitted: { label: 'Submitted', color: 'info' },
      'in progress': { label: 'In Progress', color: 'warning' },
      completed: { label: 'Completed', color: 'success' },
    };

    return <Chip size="small" label={stageMap[stage]?.label || bfsStage} color={stageMap[stage]?.color || 'primary'} />;
  };

  const notesOpen = Boolean(notesAnchorEl);
  const notesPopoverId = notesOpen ? 'notes-popover' : undefined;
  
  const cofOpen = Boolean(cofAnchorEl);
  const cofPopoverId = cofOpen ? 'cof-popover' : undefined;

  const bfsStageOpen = Boolean(bfsStageAnchorEl);
  const bfsStagePopoverId = bfsStageOpen ? 'bfs-stage-popover' : undefined;

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default }}>
      <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
        Deal Dashboard
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left Column: Deal Completion Time + BFS + Total Deals */}
        <Grid item xs={12} md={6} lg={6}>
          {/* Deal Completion Time Pie Chart */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardHeader 
              title="Deal Completion Time" 
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<WatchLaterIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              {pieChartData.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: 180 }}>
                    <ResponsiveContainer width={250} height={180}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ percent }: { percent: number }) => 
                            `${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          formatter={(value: number) => [`${value} deals`, 'Count']} 
                          contentStyle={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                        Elapsed time is measured in minutes
                      </Typography>
                      {pieChartData.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, mr: 1 }} />
                          <Typography variant="caption" sx={{ color: 'text.primary', fontSize: '0.75rem' }}>
                            {entry.name}: {entry.value} deals
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                </>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography variant="body1" color="text.secondary">
                    No completion time data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
          
          {/* BFS Submission Count Box */}
          <Card elevation={3} sx={{ bgcolor: theme.palette.primary.main, color: 'white', height: '40px', mb: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', height: '100%', p: 0.8, py: 0.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" flexDirection="column">
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1, color: 'white', fontSize: '1.1rem' }}>
                    {bfsCount}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.7rem', color: 'white' }}>
                    BFS Submissions
                  </Typography>
                </Box>
                <AssignmentTurnedInIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
            </CardContent>
          </Card>
          
          {/* Total Deals Count Box */}
          <Card elevation={3} sx={{ bgcolor: theme.palette.secondary.main, color: 'white', height: '40px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', height: '100%', p: 0.8, py: 0.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" flexDirection="column">
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1, color: 'white', fontSize: '1.1rem' }}>
                    {deals.length}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.7rem', color: 'white' }}>
                    Total Deals
                  </Typography>
                </Box>
                <BarChartIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column: Recent Deals List */}
        <Grid item xs={12} md={6} lg={6}>
          <Card elevation={3} sx={{ height: '100%' }}>

            <CardHeader 
              title="Recent Deals" 
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<AssignmentIcon color="primary" />}
              action={
                <Button size="small" onClick={handleOpenAllDeals}>
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {deals.length > 0 ? (
                <List disablePadding>
                  {deals
                    .sort((a, b) => {
                      // Sort by most recent COF or closing date
                      const dateA = a.cof || a.closing_date || a.submission_date;
                      const dateB = b.cof || b.closing_date || b.submission_date;
                      
                      if (!dateA) return 1;
                      if (!dateB) return -1;
                      
                      return new Date(dateB).getTime() - new Date(dateA).getTime();
                    })
                    .slice(0, 10)
                    .map((deal, index) => {
                      // Calculate days remaining until closing date
                      const daysRemaining = getDaysRemaining(deal.closing_date);
                      
                      // Get colors based on days remaining
                      const closingColor = getClosingDateColor(daysRemaining);
                      const iconColor = getClosingIconColor(daysRemaining);
                      
                      // Get closing status text
                      const getClosingStatusText = () => {
                        if (daysRemaining === null) return "";
                        if (daysRemaining < 0) return ` (${Math.abs(daysRemaining)} days past)`;
                        return ` (${daysRemaining} days left)`;
                      };
                      
                      return (
                        <React.Fragment key={deal.id || index}>
                          <ListItem sx={{ height: '15px', py: 0.5, px: 1, my: 0.2 }}>
                            <ListItemText
                              primary={
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item xs={4}>
                                    <Typography variant="body2" noWrap fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                                      #{deal.deal_number}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box display="flex" alignItems="center">
                                      <CalendarTodayIcon sx={{ fontSize: '0.7rem', mr: 0.5, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                        COF: {formatDate(deal.cof)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box display="flex" alignItems="center">
                                      <CalendarTodayIcon 
                                        sx={{ fontSize: '0.7rem', mr: 0.5, color: iconColor }} 
                                      />
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          color: closingColor, 
                                          fontWeight: daysRemaining !== null && daysRemaining <= 14 ? 'bold' : 'normal',
                                          fontSize: '0.65rem',
                                          whiteSpace: 'nowrap',
                                          textOverflow: 'ellipsis',
                                          overflow: 'hidden',
                                          maxWidth: '100%'
                                        }}
                                      >
                                        Closing: {formatDate(deal.closing_date)}
                                        <span style={{fontSize: '0.6rem'}}>{getClosingStatusText()}</span>
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              }
                            />
                          </ListItem>
                          {index < 9 && <Divider component="li" sx={{ my: 0.1 }} />}
                        </React.Fragment>
                      );
                    })}
                </List>
              ) : (
                <Box p={3} display="flex" justifyContent="center">
                  <Typography variant="body1" color="text.secondary">
                    No deals available
                  </Typography>
                </Box>
              )}
              <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle2" gutterBottom>Closing Date Color Legend:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: 'success.main', 
                      mr: 1 
                    }} />
                    <Typography variant="caption">More than 14 days</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: 'warning.main', 
                      mr: 1 
                    }} />
                    <Typography variant="caption">8-14 days</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: 'error.main', 
                      mr: 1 
                    }} />
                    <Typography variant="caption">7 days or less</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: 'text.disabled', 
                      mr: 1 
                    }} />
                    <Typography variant="caption">Past due</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* To-Do List */}
        <Grid item xs={12}>
          <TodoList />
        </Grid>
        
        {/* Bottom: Deal Summary Table */}
        <Grid item xs={12}>
          <DealSummaryTable 
            deals={deals}
            onNotesClick={handleNotesClick}
            onCofClick={handleCofClick}
            onBfsStageClick={handleBfsStageClick}
            onDeleteClick={handleDeleteClick}
          />
        </Grid>
      </Grid>
      
      {/* All Deals Dialog */}
      <AllDealsDialog
        open={openAllDeals}
        onClose={handleCloseAllDeals}
        deals={deals}
        onDeleteClick={handleDeleteClick}
        onNotesClick={handleNotesClick}
        onCofClick={handleCofClick}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete deal #{dealToDelete?.deal_number}? This action cannot be undone.
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This will delete all related data including:
          </Typography>
          <ul style={{ color: theme.palette.error.main, marginTop: 1 }}>
            <li>Borrower CB Notes</li>
            <li>Borrower IR Summaries</li>
            <li>UW Summaries</li>
            <li>Borrower Information</li>
            <li>Income Records</li>
            <li>Rental Properties</li>
            <li>Downpayment Sources</li>
            <li>Conditions</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Popover - updated with modern design */}
      <Popover
        id={notesPopoverId}
        open={notesOpen}
        anchorEl={notesAnchorEl}
        onClose={handleNotesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          width: 350,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Deal Notes
            </Typography>
            {currentDealForNotes && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                #{currentDealForNotes.deal_number}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ p: 2 }}>
            {notesEditMode ? (
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add notes about this deal..."
                sx={{ mb: 2 }}
              />
            ) : (
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  minHeight: '100px',
                  backgroundColor: theme.palette.grey[50],
                  whiteSpace: 'pre-wrap'
                }}
              >
                {noteText ? noteText : (
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No notes available for this deal
                  </Typography>
                )}
              </Paper>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {notesEditMode ? (
                <>
                  <Button 
                    variant="outlined"
                    onClick={toggleNotesEditMode}
                    startIcon={<EditIcon />}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<SaveIcon />} 
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<EditIcon />} 
                  onClick={toggleNotesEditMode}
                >
                  Edit Notes
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Popover>

      {/* COF Date Popover */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Popover
          id={cofPopoverId}
          open={cofOpen}
          anchorEl={cofAnchorEl}
          onClose={handleCofClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 2, width: 300 }}>
            <Typography variant="h6" gutterBottom>
              Edit COF Date
            </Typography>
            <DatePicker
              label="COF Date"
              value={selectedCofDate}
              onChange={(newDate: Date | null) => setSelectedCofDate(newDate)}
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleCofClose}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />} 
                onClick={handleSaveCof}
                disabled={savingCofDate}
              >
                {savingCofDate ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Popover>
      </LocalizationProvider>

      {/* BFS Stage Popover */}
      <Popover
        id={bfsStagePopoverId}
        open={bfsStageOpen}
        anchorEl={bfsStageAnchorEl}
        onClose={handleBfsStageClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="h6" gutterBottom>
            Edit BFS Stage
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>BFS Stage</InputLabel>
            <Select
              value={selectedBfsStage}
              label="BFS Stage"
              onChange={(e) => setSelectedBfsStage(e.target.value)}
            >
              <MenuItem value="">Not Started</MenuItem>
              <MenuItem value="Bnk Stmt Reviewed">Bnk Stmt Reviewed</MenuItem>
              <MenuItem value="Awaiting response">Awaiting response</MenuItem>
              <MenuItem value="Awaiting Docs">Awaiting Docs</MenuItem>
              <MenuItem value="Pending SDL">Pending SDL</MenuItem>
              <MenuItem value="Pending review">Pending review</MenuItem>
              <MenuItem value="N/A">N/A</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleBfsStageClose}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<SaveIcon />} 
              onClick={handleSaveBfsStage}
              disabled={savingBfsStage}
            >
              {savingBfsStage ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default Dashboard; 